import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { drainQueue } from "../lib/offlineQueue";
import { drainMediaQueue } from "../lib/mediaOfflineQueue";
import api from "../lib/api";

/**
 * Global refresh / sync button for the app header.
 *
 * Drains the offline mutation queue + the media upload queue, then reloads
 * the current route so every page re-fetches fresh data. Visible on all
 * authenticated pages (both staff and client portal).
 *
 * [Iter 265] Backend warm-up guard: BEFORE reloading, ping /api/health
 * with a tight retry loop. If the backend is mid-cold-start (5xx-ing),
 * a `window.location.reload()` would land on the login screen because
 * AuthContext's /auth/me call would fail. Now we wait up to ~15s for
 * the backend to come back, THEN reload. This kills the "Sync → kicked
 * to login" loop Dirk reported on iOS PWA.
 */
export default function SyncButton({ compact = false }) {
  const [busy, setBusy] = useState(false);

  const handleSync = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Best-effort drain of both queues. Errors are swallowed so the reload
      // still happens — offline items will retry automatically when online.
      await Promise.allSettled([drainQueue(), drainMediaQueue()]);
      // Wait for backend to be warm before reloading.
      for (let i = 0; i < 5; i++) {
        try {
          const r = await api.get("/health", { timeout: 3_000 });
          if (r?.status === 200) break;
        } catch { /* still warming, try again */ }
        await new Promise((res) => setTimeout(res, 3_000));
      }
    } finally {
      // Reload the route so all components re-fetch their data.
      window.location.reload();
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleSync}
        disabled={busy}
        className="p-1.5 rounded text-[#a0aabf] hover:text-white disabled:opacity-50"
        title="Refresh & sync"
        data-testid="sync-button-compact"
      >
        <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      </button>
    );
  }

  return (
    <button
      onClick={handleSync}
      disabled={busy}
      className="hv-btn hv-btn-ghost text-xs px-2 py-1.5 disabled:opacity-50"
      title="Pull latest data & drain the offline queue"
      data-testid="sync-button"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
      <span className="hidden lg:inline">{busy ? "Syncing…" : "Sync"}</span>
    </button>
  );
}
